/**
 * FeatureStatusBadge — displays a feature's tracker status (e.g. "To Do",
 * "In Progress") next to the feature name in `FeatureRow`.
 *
 * The status string is tracker-native and open-ended (written verbatim by the
 * feature import), so the badge renders it neutrally rather than mapping known
 * values to feedback colors. Renders nothing when `status` is
 * null/undefined/blank — a feature without a tracker status shows no badge.
 *
 * Built on the design-system `Badge` (visual sibling of `SourceIdBadge`).
 *
 * @example
 * ```tsx
 * <FeatureStatusBadge status="In Progress" />
 * <FeatureStatusBadge status={null} /> // renders nothing
 * ```
 */

import { Badge } from '@lean-agility/design-system/react';
import { cn } from '@lean-agility/design-system/react';

export interface FeatureStatusBadgeProps {
  /** Tracker-native status text; null/undefined/blank renders nothing. */
  status?: string | null;
  /** Additional CSS classes. */
  className?: string;
}

export function FeatureStatusBadge({ status, className }: FeatureStatusBadgeProps) {
  if (!status || !status.trim()) return null;

  return (
    <Badge variant="default" className={cn('shrink-0 whitespace-nowrap', className)}>
      {status}
    </Badge>
  );
}
